import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import './Teacher.css';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Typography,
  Select,
  Button,
  Input,
  Tag,
  Modal,
  Checkbox,
  Space,
  InputNumber,
  message,
  Tooltip,
  Descriptions,
  Alert
} from 'antd';
import {
  SearchOutlined,
  FileExcelOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) {
    return 'Invalid Date';
  }
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat('en-GB', options).format(date).toUpperCase();
};

const TeacherDetailsPage = () => {
  const { teacherId } = useParams();
  const { t } = useTranslation();
  const [teacher, setTeacher] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tempMonth, setTempMonth] = useState(new Date().getMonth() + 1);
  const [tempYear, setTempYear] = useState(new Date().getFullYear());
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [hourlyRates, setHourlyRates] = useState({});
  const [isApproved, setIsApproved] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [withholdingTax, setWithholdingTax] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({});
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchTeacher = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teachers/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTeacher(response.data);
    } catch (error) {
      console.error(t('errorFetchingTeacher'), error);
      message.error(t('errorFetchingTeacher'));
    } finally {
      setLoading(false);
    }
  }, [teacherId, API_URL, t]);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClasses(response.data);
      filterClasses(response.data, selectedMonth, selectedYear);
    } catch (error) {
      console.error(t('errorFetchingClasses'), error);
      message.error(t('errorFetchingClasses'));
    } finally {
      setLoading(false);
    }
  }, [teacherId, selectedMonth, selectedYear, API_URL, t]);

  const fetchApprovedClasses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teacher-hours/${teacherId}/approved`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          period: `${selectedYear}-${selectedMonth < 10 ? '0' + selectedMonth : selectedMonth}`
        }
      });
      if (response.data.length > 0) {
        setIsApproved(true);
        setFilteredClasses(response.data);
        const rates = {};
        response.data.forEach(({ class_id, hourly_rate }) => {
          rates[class_id] = hourly_rate;
        });
        setHourlyRates(rates);
      } else {
        setIsApproved(false);
      }
    } catch (error) {
      console.error(t('errorFetchingApprovedClasses'), error);
    }
  }, [teacherId, selectedMonth, selectedYear, API_URL, t]);

  const fetchClassChangeLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClassChangeLogs(response.data);
    } catch (error) {
      console.error('Error fetching class change logs:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchTeacher();
    fetchClasses();
    fetchApprovedClasses();
    fetchClassChangeLogs();
  }, [fetchTeacher, fetchClasses, fetchApprovedClasses, fetchClassChangeLogs]);

  const filterClasses = (classes, month, year) => {
    const filtered = classes.filter(cls => {
      const classDate = new Date(cls.date);
      return classDate.getMonth() + 1 === month && classDate.getFullYear() === year;
    });
    setFilteredClasses(filtered);
  };

  const handleMonthChange = (value) => {
    setTempMonth(value);
  };

  const handleYearChange = (value) => {
    setTempYear(value);
  };

  const handleSelect = () => {
    setSelectedMonth(tempMonth);
    setSelectedYear(tempYear);
    filterClasses(classes, tempMonth, tempYear);
  };

  const handleRemarkChange = (classId, value) => {
    setRemarks((prevRemarks) => ({
      ...prevRemarks,
      [classId]: value,
    }));
  };

  const convertScheduleHourToDecimal = (scheduleHour) => {
    if (!scheduleHour) return 0;
    const [hours, minutes] = scheduleHour.split('h').map(part => part.trim().replace('m', ''));
    const hoursDecimal = parseInt(hours, 10) || 0;
    const minutesDecimal = parseInt(minutes, 10) / 60 || 0;
    return hoursDecimal + minutesDecimal;
  };

  const formatDecimalToHoursMinutes = (decimalHours) => {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const handleApprove = () => {
    // Prepare data for approved classes
    const approvedClassesData = filteredClasses.map(cls => ({
      classId: cls.classid,
      hourlyRate: hourlyRates[cls.classid] || (teacher ? teacher.hoursrate : 0),
      hoursWorked: convertScheduleHourToDecimal(cls.schedule_hour),
      remark: remarks[cls.classid] || '', // Include the remark for each class
    }));

    const totalAmountWithTax = withholdingTax ? totalAmount * 0.97 : totalAmount;
    setConfirmData({
      teacherName: `${teacher.firstname} ${teacher.lastname}`,
      period: `${selectedMonth}/${selectedYear}`,
      totalAmount: formatAmount(totalAmountWithTax),
      approvedClasses: approvedClassesData, // Add the approved classes data here
    });

    setShowConfirmModal(true);
  };

  const confirmApproval = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/teacher-hours/${teacherId}/approve`, {
        period: `${selectedYear}-${selectedMonth < 10 ? '0' + selectedMonth : selectedMonth}`,
        approvedClasses: filteredClasses.map(cls => {
          const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
          return {
            classId: cls.classid,
            hourlyRate: hourlyRates[cls.classid] || (teacher ? teacher.hoursrate : 0),
            hoursWorked: scheduleHourDecimal.toFixed(2)
          };
        }),
        totalAmount: confirmData.totalAmount,
        withholdingTaxApplied: withholdingTax
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success(t('teacherHoursApprovalSaved'));
      setShowConfirmation(true);
      fetchApprovedClasses();
      setShowConfirmModal(false);
    } catch (error) {
      console.error(t('errorApprovingHours'), error);
      message.error(t('errorApprovingHours'));
    }
  };

  const getChangeReasonDisplay = (reason) => {
    switch (reason) {
      case 'admin_adjust':
        return { text: 'Admin', color: '#005181' };
      case 'cancelled_by_student':
        return { text: 'CL by S.', color: 'red' };
      case 'cancelled_by_teacher':
        return { text: 'CL by T.', color: 'orange' };
      case 'class_on_hold_by_student':
        return { text: 'On hold by S.', color: 'blue' };
      case 'class_on_hold_by_school':
        return { text: 'On hold by P.', color: 'green' };
      case 'cancel_same_day':
        return { text: 'Cancel Same Day', color: '#FF4500' };
      case 'make_up':
        return { text: 'Make Up', color: '#8A2BE2' };
      case 'others':
        return { text: 'Others', color: 'darkgrey' };
      default:
        return { text: reason, color: 'black' };
    }
  };

  const handleExportCSV = () => {
    const csvData = filteredClasses.map(cls => ({
      'Teacher ID': teacher ? teacher.userid : '',
      'Teacher Name': teacher ? `${teacher.firstname} ${teacher.lastname}` : '',
      'Class Code': cls.class_code,
      'Class ID': cls.classid,
      'Subject Name': cls.subject_name,
      'Date': formatDate(cls.date),
      'Schedule Hour': cls.schedule_hour,
      'Hourly Rate': hourlyRates[cls.classid] || (teacher ? teacher.hoursrate : 0),
      'Total Hours': convertScheduleHourToDecimal(cls.schedule_hour).toFixed(2),
      'Total Amount': formatAmount(convertScheduleHourToDecimal(cls.schedule_hour) * (hourlyRates[cls.classid] || (teacher ? teacher.hoursrate : 0))),
      'Status': cls.status,
      'Payout': cls.payout
    }));
    return csvData;
  };

  const handleRateChange = (classId, value) => {
    setHourlyRates({
      ...hourlyRates,
      [classId]: parseFloat(value) || 0
    });
  };

  const totalScheduleHours = filteredClasses.reduce((acc, cls) => acc + convertScheduleHourToDecimal(cls.schedule_hour), 0);
  const totalAmount = filteredClasses.reduce((acc, cls) => {
    const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
    const hourlyRate = hourlyRates[cls.classid] || (teacher ? teacher.hoursrate : 0);
    return acc + (scheduleHourDecimal * hourlyRate);
  }, 0);

  const formatAmount = (amount) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const allClassesApproved = filteredClasses.every(cls => cls.status === 'approved');

  // Define columns for Ant Design Table
  const columns = [
    {
      title: t('classCode'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 120,
    },
    {
      title: t('id'),
      dataIndex: 'classid',
      key: 'classid',
      width: 80,
    },
    {
      title: t('subjectName'),
      dataIndex: 'subject_name',
      key: 'subject_name',
      ellipsis: true,
    },
    {
      title: t('type'),
      key: 'type',
      width: 90,
      render: (_, record) => (
        record.isprivate ? t('private') : t('group')
      ),
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: t('hours'),
      dataIndex: 'schedule_hour',
      key: 'schedule_hour',
      width: 90,
    },
    {
      title: t('rate'),
      key: 'rate',
      width: 100,
      render: (_, record) => (
        <InputNumber
          min={0}
          value={hourlyRates[record.classid] || (teacher ? teacher.hoursrate : 0)}
          onChange={(value) => handleRateChange(record.classid, value)}
          disabled={isApproved}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('change'),
      key: 'change',
      width: 100,
      render: (_, record) => {
        const classChangeLog = classChangeLogs.find(log => log.classid === record.classid);
        const changeReason = getChangeReasonDisplay(classChangeLog?.changereason);
        return (
          <Tag color={changeReason.color}>{changeReason.text}</Tag>
        );
      },
    },
    {
      title: t('note'),
      key: 'note',
      width: 120,
      render: (_, record) => {
        const classChangeLog = classChangeLogs.find(log => log.classid === record.classid);
        return classChangeLog?.note || '-';
      },
    },
    {
      title: t('total'),
      key: 'total',
      width: 100,
      render: (_, record) => {
        const scheduleHourDecimal = convertScheduleHourToDecimal(record.schedule_hour);
        const hourlyRate = hourlyRates[record.classid] || (teacher ? teacher.hoursrate : 0);
        const totalAmount = scheduleHourDecimal * hourlyRate;
        return isNaN(totalAmount) ? 'N/A' : formatAmount(totalAmount);
      },
    },
    {
      title: t('confirmteach'),
      key: 'status',
      width: 140,
      render: (_, record) => (
        <Tag color={record.status === 'approved' ? 'green' : 'orange'}>
          {record.status === 'approved' ? t('HOURSAPPROVED') : t('confirmpending')}
        </Tag>
      ),
    },
    {
      title: t('admin'),
      key: 'adminapprove',
      width: 140,
      render: (_, record) => (
        <Tag color={record.adminapprove === 'adminconfirm' ? 'green' : 'orange'}>
          {record.adminapprove === 'adminconfirm' ? t('adminconfirmed') : t('confirmpending')}
        </Tag>
      ),
    },
    {
      title: t('status'),
      key: 'payout',
      width: 100,
      render: (_, record) => (
        <Tag color={record.payout === 'pending' ? 'orange' : 'green'}>
          {record.payout === 'pending' ? t('Pending') : t('Paid')}
        </Tag>
      ),
    },
    {
      title: t('remark'),
      key: 'remark',
      width: 200,
      render: (_, record) => (
        <Input
          value={remarks[record.classid] || ''}
          onChange={(e) => handleRemarkChange(record.classid, e.target.value)}
          placeholder={t('enterRemark')}
        />
      ),
    },
  ];

  const summaryContent = () => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={5}>
          <Text strong>{t('total')}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5}>
          <Text strong>{formatDecimalToHoursMinutes(totalScheduleHours)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} colSpan={3}></Table.Summary.Cell>
        <Table.Summary.Cell index={9}>
          <Title level={4}>{formatAmount(totalAmount)}</Title>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={10} colSpan={4}></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="teacher-details-container">
        <Card title="Teacher Details" className="teacher-details-card" loading={loading}>
          
          {teacher && (
            <>
              <Descriptions title={`(${teacher.nickname}) ${teacher.firstname} ${teacher.lastname}`} bordered>
                <Descriptions.Item label={t('teacherID')}>{teacher.userid}</Descriptions.Item>
                <Descriptions.Item label={t('currentHourlyRate')}>{teacher.hoursrate}</Descriptions.Item>
              </Descriptions>
              
              <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <Space size="middle">
                  <Select
                    value={tempMonth}
                    onChange={handleMonthChange}
                    style={{ width: 120 }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <Option key={month} value={month}>
                        {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    value={tempYear}
                    onChange={handleYearChange}
                    style={{ width: 100 }}
                  >
                    {Array.from({ length: 5 }, (_, i) => 2024 + i).map(year => (
                      <Option key={year} value={year}>
                        {year}
                      </Option>
                    ))}
                  </Select>
                  
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />} 
                    onClick={handleSelect}
                  >
                    {t('search')}
                  </Button>
                  
                  <Tooltip title={t('exportToCSV')}>
                    <CSVLink
                      data={handleExportCSV()}
                      filename={`approved_hours_${teacherId}_${selectedMonth}_${selectedYear}.csv`}
                    >
                      <Button 
                        icon={<FileExcelOutlined />}
                      >
                        {t('exportToCSV')}
                      </Button>
                    </CSVLink>
                  </Tooltip>
                </Space>
              </div>
              
              <Table
                columns={columns}
                dataSource={filteredClasses}
                rowKey="classid"
                scroll={{ x: 'max-content' }}
                pagination={false}
                size="middle"
                bordered
                summary={summaryContent}
              />
              
              {allClassesApproved && !isApproved && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleApprove}
                  >
                    {t('approveHoursAndPayment')}
                  </Button>
                </div>
              )}
              
              {showConfirmation && (
                <Alert
                  message={t('teacherHoursApprovalSaved')}
                  type="success"
                  showIcon
                  closable
                  onClose={() => setShowConfirmation(false)}
                  style={{ marginTop: '20px' }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      <Modal
        title={<Title level={4}>{t('confirm_payment')}</Title>}
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>
            {t('cancel')}
          </Button>,
          <Button key="confirm" type="primary" onClick={confirmApproval}>
            {t('confirm')}
          </Button>
        ]}
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('teacherName')}>{confirmData.teacherName}</Descriptions.Item>
          <Descriptions.Item label={t('period')}>{confirmData.period}</Descriptions.Item>
          <Descriptions.Item label={t('nettotal')}>{confirmData.totalAmount}</Descriptions.Item>
        </Descriptions>
        
        <div style={{ marginTop: '16px' }}>
          <Checkbox 
            checked={withholdingTax} 
            onChange={() => setWithholdingTax(!withholdingTax)}
          >
            {t('sumWithholdingTax')}
          </Checkbox>
        </div>
      </Modal>
    </div>
  );
};

export default withRoleAccess(TeacherDetailsPage, ['superadmin']);